/**
 * Checkout helpers shared by the checkout page and its order draft.
 *
 * Kept deliberately pure: validation returns raw `frontend.checkout_flow.*`
 * translation keys (the page renders them with t()), and buildCheckoutDraft
 * only assembles a user-facing message string. No network, no storage, no
 * logging — the Telegram hand-off is done by the parent, never here.
 */

import type { ShippingPrices } from '@/types'

/** Where orders are confirmed and paid for. */
export const TELEGRAM_CONTACT = 'https://t.me/cyberjosef'

export type CheckoutForm = {
  name: string
  phone: string
  email: string
  country: string
  city: string
  address: string
  region: string
  postal: string
  comment: string
  consent: boolean
}

/** Countries whose national postal code is exactly six digits. */
const LOCAL_POSTAL = ['BY', 'RU', 'KZ']

/** A display currency must never imply a destination or invent an upfront fee. */
export function shippingForCountry(country: string, prices: ShippingPrices): Record<string, number> | null {
  const code = country.trim().toUpperCase()
  if (!code) return null
  return prices[code === 'BY' || code === 'RU' ? code : 'other']
}

/**
 * Validate a checkout form. Returns a map of field → raw translation error
 * key; an empty object means the form is valid.
 *
 * Name accepts any single word of two characters or more (the network is
 * worldwide, so we never demand two words). Phone is an international format:
 * a leading "+", then digits, spaces, parentheses and hyphens, for a total of
 * 7–15 digits. Email is optional but must look like an address if filled.
 * Country is a two-letter ISO code. Postal is mandatory (six digits) for BY,
 * RU and KZ and optional elsewhere.
 */
export function validateCheckout(data: CheckoutForm): Partial<Record<keyof CheckoutForm, string>> {
  const errors: Partial<Record<keyof CheckoutForm, string>> = {}

  if (data.name.trim().length < 2) errors.name = 'name_required'

  const phone = data.phone.trim()
  const digits = phone.replace(/\D/g, '')
  const phoneShape = /^\+[0-9\s()-]+$/.test(phone)
  if (!phoneShape || digits.length < 7 || digits.length > 15) errors.phone = 'phone_invalid'

  const email = data.email.trim()
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'email_invalid'

  const country = data.country.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(country)) errors.country = 'country_required'

  if (data.city.trim().length < 2) errors.city = 'city_required'
  if (data.address.trim().length < 5) errors.address = 'address_required'

  const postal = data.postal.trim()
  if (LOCAL_POSTAL.includes(country)) {
    if (!/^\d{6}$/.test(postal)) errors.postal = 'postal_local_invalid'
  } else if (postal !== '' && !/^[A-Za-z0-9\s-]{1,16}$/.test(postal)) {
    errors.postal = 'postal_invalid'
  }

  if (!data.consent) errors.consent = 'consent_required'

  return errors
}

/**
 * Assemble the message a customer sends to the workshop over Telegram.
 *
 * `lines` are item descriptions already formatted by the parent (name, count,
 * price). `t` looks up unprefixed `frontend.checkout_flow.*` keys and applies
 * %{var} interpolation, matching the site i18n. Optional fields (email,
 * region, postal, comment) are only included when filled. Pure: no send, no
 * network, no logging.
 */
export function buildCheckoutDraft(
  lines: string[],
  shipping: string | null,
  total: string | null,
  data: CheckoutForm,
  countryName: string,
  t: (key: string, vars?: Record<string, string | number>) => string
): string {
  const rows: string[] = [t('draft_intro')]

  for (const line of lines) {
    if (line.trim() !== '') rows.push(line)
  }

  if (shipping !== null) rows.push(t('draft_shipping', { value: shipping }))
  if (total !== null) rows.push(t('draft_total', { value: total }))
  rows.push(t('draft_name', { value: data.name.trim() }))
  rows.push(t('draft_phone', { value: data.phone.trim() }))

  const email = data.email.trim()
  if (email !== '') rows.push(t('draft_email', { value: email }))

  rows.push(t('draft_country', { value: countryName }))
  rows.push(t('draft_city', { value: data.city.trim() }))

  const region = data.region.trim()
  if (region !== '') rows.push(t('draft_region', { value: region }))

  rows.push(t('draft_address', { value: data.address.trim() }))

  const postal = data.postal.trim()
  if (postal !== '') rows.push(t('draft_postal', { value: postal }))

  const comment = data.comment.trim()
  if (comment !== '') rows.push(t('draft_comment', { value: comment }))

  return rows.join('\n')
}
